import {apsEnums} from "../../shared/dto/aps.enums";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import CheckListOverrideModule, {CheckItem} from "./checkListOverride.module";
import GobUtil from "../../services/utility/gob.utility";
import {apsCommands} from "../../shared/dto/aps.commands";
import * as _ from "lodash";
import {AuthDataService} from "../../services/auth/authData.service";
import { DefectType } from "../defectType";

interface CheckInstanceItem extends apsViewModels.CheckInstanceItem {
  aggregateFunction: string;
}
  
export class CheckInstance implements apsCommands.FillOutCheckResult {
  id: string;
  checkId: string;
  status: apsEnums.CheckStatus;
  result: any;
  total: string;
  machineId: string;
  section: number;
  gob: number;
  comment: string;
  aggregateValues: number[];
  sequence: number;
  validationRangeMin?: number;
  validationRangeMax?: number;
  defectTypes?: DefectType[];
  additionalData: string | null;
  additionalDataType?: apsEnums.CheckAdditionalData;

  private aggregateCheckIds: string[] | null;

  public step: number;
  public stepError: string;
  public isNumeric: boolean;
  public hasRange: boolean;
  public additionalDataModel: any;

  public showName: boolean;
  public showHeading: boolean;
  public checkItem: CheckItem;
  private machineConfiguration: apsViewModels.MachineConfiguration;
  private jobRun: apsViewModels.JobRunConfiguration;

  constructor(
    checkList: CheckListInstance,
    data: apsViewModels.CheckInstanceItem,
    check: CheckItem,
    machine: apsViewModels.MachineConfiguration,
    jobRun: apsViewModels.JobRunConfiguration) {
    _.assign(this, data);
    this.checkItem = check;
    this.machineConfiguration = machine || <apsViewModels.MachineConfiguration>{};
    this.jobRun = jobRun;
    this.status = data.status || apsEnums.CheckStatus.NotDone;
    this.validationRangeMin = this.checkItem.rangeMin;
    this.validationRangeMax = this.checkItem.rangeMax;
    this.aggregateCheckIds = null;
    this.additionalDataType = this.getAdditionalDataType();
    this.additionalDataModel = this.setAdditionalDataModel();

    if (checkList.status <= apsEnums.CheckListStatus.Complete && check.prepopulate && !this.result) {
      switch (check.dataType) {
        case apsEnums.CheckDataType.DateTime:
          var d = new Date();
          d.setSeconds(0);
          d.setMilliseconds(0);
          this.result = d;
          break;
        case apsEnums.CheckDataType.Count:
          this.total = check.frequencyTotal.toString();
          break;
      }
    }

    this.isNumeric = check.dataType === apsEnums.CheckDataType.Integer
      || check.dataType === apsEnums.CheckDataType.Decimal
      || check.dataType === apsEnums.CheckDataType.Percent
      || check.dataType === apsEnums.CheckDataType.Count;

    this.hasRange = this.isNumeric && _.isNumber(check.rangeMin) && _.isNumber(check.rangeMax);

    this.step = 1; // int and count
    this.stepError = 'Must be an integer';

    if (check.dataType === apsEnums.CheckDataType.Decimal || check.dataType === apsEnums.CheckDataType.Percent) {
      let places = check.numberOfDecimalPlaces || 2;
      for (let place = 0; place < places; place++) {
        this.step = this.step / 10.0;
      }
      this.stepError = 'Up to ' + places + ' decimal places are allowed';
    }
  }

  getAdditionalDataType(): apsEnums.CheckAdditionalData | null {  
    if (this.checkItem.dataType === apsEnums.CheckDataType.Boolean) {
      if (this.result === true || this.result === 'true')
        return this.checkItem.trueAdditionalData;

      if (this.result === false || this.result === 'false')
        return this.checkItem.falseAdditionalData;
    }

    if (this.checkItem.choices !== null && this.checkItem.choices.length > 1) {
      let choice = this.checkItem.choices.filter(x => x.value === this.result);
      if (!!choice && choice.length > 0) {
        return choice[0].additionalData;
      }
    }

    return null;
  }

  prepareForSubmit(): void {
    if (this.additionalDataType === null 
      || this.additionalDataType === apsEnums.CheckAdditionalData.None
      || !this.additionalDataModel) {
      this.additionalData = null;
    } else {
      this.additionalData = JSON.stringify(this.additionalDataModel);
    }
  }

  private setAdditionalDataModel(): any {
    if (!this.additionalData
        || this.additionalData.length < 1
    ) {
      return {};
    }

    return JSON.parse(this.additionalData);
  }

  private calculateAggregate(aggregateFunction: apsEnums.AggregateFunction, checkList?: CheckListInstance) {
    let aggregateValues = this.aggregateValues;
    if (this.checkItem.aggregateCheckId !== null && checkList) {
      let previousValues = checkList.previousCheckValues.filter(x => x.checkId === this.checkItem.aggregateCheckId
        && x.frequency === this.checkItem.aggregateFrequencyUnits)
        .map(x => x.instances)
        .reduce((a,b) => a.concat(b), []).filter(x => 
          (this.machineId === null || x.machineId === this.machineId)
          && (this.section === null || x.section === this.section)
          && (this.gob === null || x.gob === this.gob)
          && (this.sequence === null || x.sequence === this.sequence))
        .map(x => x.result);
      aggregateValues = checkList.results.filter(x => 
          x.checkId === this.checkItem.aggregateCheckId
          && (this.machineId === null || x.machineId === this.machineId)
          && (this.section === null || x.section === this.section)
          && (this.gob === null || x.gob === this.gob)
          && (this.sequence === null || x.sequence === this.sequence))
        .map(x => x.result).concat(previousValues);
    }

    if (_.some(aggregateValues, (x) => _.isUndefined(x) || _.isNull(x) || (<any>x) === 'null')) {
      return '';
    }

    var result;

    let numericValues = aggregateValues.map(x => _.isNumber(x) ? x : parseFloat(x));

    switch (aggregateFunction) {
      case apsEnums.AggregateFunction.Average:
        result = _.sum(numericValues) / aggregateValues.length;
        break;
      case apsEnums.AggregateFunction.Min:
        result = _.min(numericValues);
        break;
      case apsEnums.AggregateFunction.Max:
        result = _.max(numericValues);
        break;
      case apsEnums.AggregateFunction.Sum:
      case apsEnums.AggregateFunction.Cumulation:
        result = _.sum(numericValues);
        break;
      default:
        return '';
    }

    return (Math.round(result * 100) / 100).toString();
  }

  onChange(checkList: CheckListInstance) {
    if (!this.aggregateCheckIds) {
      this.aggregateCheckIds = checkList.checkList().checks.filter(x => x.aggregateCheckId === this.checkId).map(x => x.id);
    }
    if (this.aggregateCheckIds.length === 0) {
      return;
    }

    let aggregates = checkList.results.filter(x => this.aggregateCheckIds.some(id => id === x.checkId));
    for (const check of aggregates) {
      let result = check.aggregateResult(null, checkList);
      if (result !== check.result) {
        check.result = result;
        check.onChange(checkList);
      }
    }
  }

  aggregateResult(checkDim?: apsViewModels.CheckDimensionItem, checkList?: CheckListInstance): string {
    if (this.checkItem.valueType === apsEnums.CheckValueType.MultipleSample) {
      return this.calculateAggregate(checkDim.aggregateFunction, checkList);
    } else {
      return this.calculateAggregate(this.checkItem.aggregateFunction, checkList);
    }
  }

  booleanChanged(): void {
    if (this.checkItem.dataType !== apsEnums.CheckDataType.Boolean) {
      return;
    }

    this.updateAdditionalDataType();

    if (this.checkItem.booleanPass !== null) {
      if (this.result === this.checkItem.booleanPass.toString()) {
        this.status = apsEnums.CheckStatus.Passed;
      } else if (this.result === (!this.checkItem.booleanPass).toString()) {
        this.status = apsEnums.CheckStatus.Failed;
      }
    }
  }

  choiceChanged(): void {
    if (!this.checkItem.choices || this.checkItem.choices.length === 0) {
      return;
    }

    this.updateAdditionalDataType();

    let choice = this.checkItem.choices.filter(x => x.value === this.result);
    if (!!choice && choice.length > 0) {
      let outcome = choice[0].outcome;
      if (outcome) {
        this.status = outcome;
      }
    }
  }

  updateAdditionalDataType(): void {
    let additionalDataType = this.getAdditionalDataType();
    if (additionalDataType !== this.additionalDataType) {
      this.additionalDataModel = {};
      this.additionalDataType = additionalDataType;
    }
  }

  getSection(jobRun: apsViewModels.JobRunConfiguration): string {
    if (this.section && jobRun) {
      return (((this.machineConfiguration.number - 1) * jobRun.numberOfSections) + this.section).toString();
    }
    return '';
  }

  check() {
    return this.checkItem;
  }

  machine() {
    return this.machineConfiguration;
  }

  gobName() {
    if (this.gob && this.jobRun) {
      return GobUtil.getGobName(this.gob, this.jobRun.numberOfGobs);
    }
    return undefined;
  }

  dimensionName() {
    if (!this.checkItem.dimensions
      || this.checkItem.valueType === apsEnums.CheckValueType.MultipleSample
      || this.checkItem.dimensions.length < (this.sequence + 1)) {
      return null;
    }

    return this.checkItem.dimensions[this.sequence].name;
  }

  private getMold(molds: apsViewModels.MachineMold[]): apsViewModels.MachineMold {
    if (!molds || molds.length === 0 || !this.gob || !this.section || !this.machineId) {
      return null;
    }
    var mold = molds.filter(x => x.isMachineId === this.machineId
      && x.section === this.section
      && x.gob === this.gob);
    if (mold.length === 0) {
      return null;
    }
    return mold[0];
  }

  hasMold(molds: apsViewModels.MachineMold[]) {
    return this.getMold(molds) !== null;
  }

  moldNumber(molds: apsViewModels.MachineMold[]) {
    let mold = this.getMold(molds);
    if (mold) {
      return mold.moldNumber;
    }
    return null;
  }

  isStatusInvalid() {
    if (!this.checkItem.isMandatory) {
      return false;
    }

    return !this.status || this.status === apsEnums.CheckStatus.NotDone;
  }

  isRequired() {
    return (this.status === apsEnums.CheckStatus.Passed
      || this.status === apsEnums.CheckStatus.Failed);
  }

  autoStatus(checkList: CheckListInstance) {
    if (this.checkItem.dataType === apsEnums.CheckDataType.Count) {
      if (this.result && this.total) {
        var failPercent = this.checkItem.frequencyFail / this.checkItem.frequencyTotal;
        var actualPercent = this.result / +this.total;
        if (actualPercent >= failPercent) {
          this.status = apsEnums.CheckStatus.Failed;
        } else {
          this.status = apsEnums.CheckStatus.Passed;
        }
      } else {
        this.status = apsEnums.CheckStatus.NotDone;
      }
    }
    else if (!!this.checkItem.passFailRangeCheckId && this.result) {
      let rangeCheck = checkList.results.filter(x => x.checkId === this.checkItem.passFailRangeCheckId);
      if (rangeCheck.length > 0 && !!rangeCheck[0].result && typeof(rangeCheck[0].result) === 'string') {
        let range = (<string>rangeCheck[0].result).split("-");
        if (range.length === 2) {
          try {
            let min = parseFloat(range[0]);
            let max = parseFloat(range[1]);
            if (min <= this.result && max >= this.result) {
              this.status = apsEnums.CheckStatus.Passed;
            } else {
              this.status = apsEnums.CheckStatus.Failed;
            }
          }
          catch {
            // ignore parse error
          }
        }
      }
    }
    return this.status;
  }
}

export default class CheckListInstance implements apsCommands.FillOutCheckList {
  id: string;
  results: CheckInstance[];
  checkListId: string;
  shopId: string;
  tankId: string;
  plantId: string;
  plantLocationId: string;
  status: apsEnums.CheckListStatus;
  isMandatory: boolean;
  scheduledOn: Date;

  private checkListItem: apsViewModels.CheckListItem;
  private shop: apsViewModels.ShopConfiguration;
  private plant: apsViewModels.PlantItem;
  private jobRun: apsViewModels.JobRunConfiguration;
  private existingChecks: apsViewModels.CheckInstanceItem[];
  previousCheckValues: apsViewModels.PreviousCheckInstanceItem[];

  constructor(data: apsViewModels.CheckListInstanceItem) {
    this.id = data.id;
    this.checkListId = data.checkListId;
    this.shopId = data.shopId;
    this.tankId = data.tankId;
    this.plantId = data.plantId;
    this.status = data.status;
    this.isMandatory = data.isMandatory;
    this.scheduledOn = data.scheduledOn;
    this.results = [];
    this.existingChecks = data.checkInstances;
    this.plantLocationId = data.plantLocationId;
    this.previousCheckValues = data.previousCheckInstances;
  }

  checkList() {
    return this.checkListItem;
  }

  populateResults(checkList: apsViewModels.CheckListItem,
                  plant: apsViewModels.PlantItem,
                  shop: apsViewModels.ShopConfiguration,
                  authDataService: AuthDataService): void {
    this.checkListItem = checkList;
    this.plant = plant;
    this.shop = shop;
    this.jobRun = shop && shop.jobRuns ? _.last(shop.jobRuns) : null;
    _.forEach(_.sortBy(checkList.checks, 'order'), (check: apsViewModels.CheckItem) => {
      var checkItem = CheckListOverrideModule.extendCheckItem(check);
      CheckListOverrideModule.setOverrideValue(checkItem, plant.id, shop ? shop.id : null,
        this.jobRun ? this.jobRun.id : null, authDataService);
      this.populateCheck(checkItem);
    });

    var prevHeading: string = '';
    var prevName: string = '';
    _.forEach(this.results, (item: CheckInstance) => {
      item.showHeading = item.check().heading !== prevHeading;
      item.showName = item.check().name !== prevName;
      prevHeading = item.check().heading;
      prevName = item.check().name;
    });
  }

  private populateCheck(check: CheckItem): void {
    switch (check.entry) {
      case apsEnums.CheckEntry.PerMachine:
        this.populateMachineCheck(check);
        return;
      case apsEnums.CheckEntry.PerSection:
        this.populateSectionCheck(check);
        return;
      case apsEnums.CheckEntry.PerCavity:
        this.populateCavityCheck(check);
        return;
      case apsEnums.CheckEntry.PerDrop:
        this.populateDropCheck(check);
        return;
      default:
        this.populateShopCheck(check);
    }
  }

  private addCheckDimension(
    check: CheckItem,
    machine?: apsViewModels.MachineConfiguration,
    section?: number,
    gob?: number,
    sequence?: number): void {

    var machineId = machine ? machine.id : null;
    section = section || null;
    gob = gob || null;

    if (check.valueType === apsEnums.CheckValueType.MultipleSample) {
      sequence = 0;
    }

    var checkInstance: CheckInstanceItem = <CheckInstanceItem>_.find(this.existingChecks, {
      checkId: check.id,
      machineId: machineId,
      section: section,
      gob: gob,
      sequence: sequence
    });

    if (!checkInstance) {
      checkInstance = {
        id: null,
        status: check.defaultOutcome,
        result: this.getDefaultResult(check),
        total: null,
        checkId: check.id,
        machineId: machineId,
        section: section,
        gob: gob,
        sequence: sequence,
        allowComments: false,
        comment: null,
        aggregateFunction: null,
        aggregateValues: [],
        additionalData: null
      };

      if (check.aggregatesCount > 1) {
        checkInstance.aggregateValues = new Array(check.aggregatesCount);
      }
    } else if (!checkInstance.result) {
      checkInstance.result = this.getDefaultResult(check);
    }

    if (check.aggregateFunction) {
      checkInstance.aggregateFunction = _.startCase(apsEnums.AggregateFunction[check.aggregateFunction]);
    }

    this.results.push(new CheckInstance(this, checkInstance, check, machine, this.jobRun));
  }

  private getDefaultResult(check: CheckItem): string {
    var isBooleanWithDefault = check.dataType === apsEnums.CheckDataType.Boolean
      && check.booleanPass !== null
      && check.defaultOutcome
      && check.defaultOutcome !== apsEnums.CheckStatus.NotDone;
    if (!isBooleanWithDefault) {
      return null;
    }
    if (check.defaultOutcome === apsEnums.CheckStatus.Passed) {
      return check.booleanPass.toString();
    }
    if (check.defaultOutcome === apsEnums.CheckStatus.Failed) {
      return (!check.booleanPass).toString();
    }
    if (check.defaultOutcome === apsEnums.CheckStatus.NotApplicable) {
      return 'N/A';
    }
    return null;
  }

  private populateShopCheck(check: CheckItem): void {
    this.addCheck(check);
  }

  private populateMachineCheck(check: CheckItem): void {
    _.forEach(this.shop.machineConfigurations, (machine: apsViewModels.MachineConfiguration) => {
      this.addCheck(check, machine);
    });
  }

  private populateSectionCheck(check: CheckItem): void {
    _.forEach(this.shop.machineConfigurations, (machine: apsViewModels.MachineConfiguration) => {
      _.forEach(_.range(1, this.jobRun.numberOfSections + 1), (section: number) => {
        this.addCheck(check, machine, section);
      });
    });
  }

  private populateCavityCheck(check: CheckItem): void {
    _.forEach(this.shop.machineConfigurations, (machine: apsViewModels.MachineConfiguration) => {
      _.forEach(_.range(1, this.jobRun.numberOfSections + 1), (section: number) => {
        _.forEach(_.range(1, this.jobRun.numberOfGobs + 1), (gob: number) => {
          this.addCheck(check, machine, section, gob);
        });
      });
    });
  }

  private populateDropCheck(check: CheckItem): void {
    _.forEach(this.shop.machineConfigurations, (machine: apsViewModels.MachineConfiguration) => {
      _.forEach(_.range(1, this.jobRun.numberOfGobs + 1), (gob: number) => {
        this.addCheck(check, machine, undefined, gob);
      });
    });
  }

  private addCheck(
    check: CheckItem,
    machine?: apsViewModels.MachineConfiguration,
    section?: number,
    gob?: number) {
      if (check.dimensions.length === 0 || check.valueType === apsEnums.CheckValueType.MultipleSample) {
        this.addCheckDimension(check, machine, section, gob, null);
      } else {
        for (var i = 0; i < check.dimensions.length; i++) {
          this.addCheckDimension(check, machine, section, gob, i);
        }
      }
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--5!./src/app/models/checkLists/checkListInstance.ts